import FormControl from '@material-ui/core/FormControl';
import Typography from '@material-ui/core/Typography';
import Modal from '@material-ui/core/Modal';
import { createStyles, FormGroup, makeStyles, Theme } from '@material-ui/core';

import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import BaseForm from './base-create-form';
import PasswordInputComponent from '../../form-elements/password-input';
import StandardInputComponent from '../../form-elements/standard-input';
import SelectMenuComponent from '../../form-elements/select-menu';
import CheckboxInputComponent from '../../form-elements/checkbox-input';
import SubmitButton from '../../form-elements/submit-button';

import { newUser, setAppLoadingState } from '../../../store/index';
import { registerIndividualUser } from '../../../services';
import { IIndividualFormData } from '../../../services/interfaces';
import { sendEmailVerification } from '../../../services/auth/service';

import { baseTheme } from '../../../theme';

import { timezones } from '../account-form-utils';
import { errors } from '../../../contants/errors';
import { ROUTE_PATHS } from '../../../contants/ROUTE_PATHS';
import TermsAndConditionsModal from './terms-and-conditions-modal';
import firebase from 'firebase';

interface IndividualCreateForm {
  submitText?: string;
  themeStyle?: 'dark' | 'light';
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    inputBase: {
      backgroundColor: '#fff',
      color: theme.palette.primary.main,
      width: '100%',
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: '10px',
    },
    base: {
      width: '100%',
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      marginLeft: 'auto',
      marginRight: 'auto',
      textAlign: 'center',
      display: 'inline',
    },
    submitButton: {
      fontSize: '2em',
    },
    dark: {
      backgroundColor: 'transparent',
    },
    light: {
      backgroundColor: theme.palette.primary.main,
      padding: theme.spacing(3),
      borderRadius: '10px',
    },
    passwordResetLinksWrapper: {
      padding: theme.spacing(1),
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    passwordResetLinksButton: {
      fontSize: '1.5em',
      backgroundColor: theme.palette.primary.main,
      padding: theme.spacing(2),
      borderRadius: '10px',
      color: theme.palette.background.paper,
      'text-decoration': 'none',
      '&:hover': {
        backgroundColor: theme.palette.secondary.main,
      },
    },
    modal: {
      position:'absolute',
      top:'10%',
      left:'10%',
      overflow:'scroll',
      height:'100%',
      display:'block',
    },
    termsAndConditionsLink: {
      '&:hover': {
        color: theme.palette.secondary.main,
      },
      textDecoration: 'underline',
      display: 'inline',
    },
  }),
);

export default function IndividualCreateForm(props: IndividualCreateForm) {
  const { submitText = 'Create Account', themeStyle = 'dark' } = props;
  const classes = useStyles(baseTheme);
  const history = useHistory();
  const dispatch = useDispatch();
  const defaultFormValues: IIndividualFormData = {
    displayName: '',
    password: '',
    confirmPass: '',
    email: '',
    contactEmail: '',
    firstName: '',
    lastName: '',
    timezone: '',
    referal: '',
    agreeTermsConditions: false,
  };

  const [formValues, setFormValues] = useState(defaultFormValues);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showPasswordResetLink, setShowPasswordResetLink] = useState<boolean>(false);
  const [showTermsAndConditions, setShowTermsAndConditions] = useState<boolean>(false);
  const themerizer = themeStyle === 'light' ? classes.light : classes.dark;

  const handleChange = (prop: any) => (event: any) => {
    setFormValues({ ...formValues, [prop]: event.target.value });
  };

  const handleCheckChange = (event: any) => {
    setFormValues({ ...formValues, [event.target.name]: event.target.checked });
  };
  const formValuesEmpty = () => {
    return (
        formValues.displayName.length &&
        formValues.password.length &&
        formValues.confirmPass &&
        formValues.email.length &&
        formValues.firstName.length &&
        formValues.lastName.length &&
        formValues.timezone.length &&
        formValues.referal.length
    );
  };
  const emailValid = () => {
    return formValues.email.match(/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
  };
  const passwordMatchValidation = () => {
    return formValues.password === formValues.confirmPass;
  };
  const passwordLengthValidation = () => {
    return formValues.password.length > 7;

  };
  const passwordFormatValidation = () => {
    // password contains at least 1 letter, 1 number, 1 special character
    return formValues.password.match(/(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])/);
  };
  const signUpFormValidation = async () => {
    if (!formValuesEmpty()) {
      throw new Error('All fields require an entry.');
    }
    if (!formValues.agreeTermsConditions) {
      throw new Error('Please agree to Terms and Conditions.');
    }
    if (!emailValid()) {
      throw new Error('Email does not match standard format.');
    }
    if (!passwordMatchValidation()) {
      throw new Error('Password and Confirm Password do not match.');
    }
    if (!passwordLengthValidation()) {
      throw new Error('Password must be at least 8 characters.');
    }
    if (!passwordFormatValidation()) {
      throw new Error('Password must contain letters & numbers & at least one special character');
    }
  };
  const handleSubmit = async () => {
    try {
      await signUpFormValidation();
      // @ts-ignore
      window.recaptchaVerifier = new firebase.auth.RecaptchaVerifier(
        'recaptcha',
        {
          callback: (response: { ok: any; }) => {
            return submitForm();
          },
          'expired-callback': () => {
            return null;
          },
        });
      // @ts-ignore
      window.recaptchaVerifier.render();
    } catch (e) {
      console.log(e);
      setErrorMessage(e.message);
      setShowPasswordResetLink(e.code === errors.emailAddressAlreadyInUse.code);
      setAppLoadingState(dispatch, false);
    }
  };
  const submitForm = async () => {
    try {
      setAppLoadingState(dispatch, true);
      dispatch(newUser(true));
      await registerIndividualUser(formValues);
      await sendEmailVerification();
      history.push('/admin');
      setAppLoadingState(dispatch, false);
    } catch (error) {
      console.log(error);
      const errorMessageToDisplay: string = error.message ? error.message : 'There as an issue registering your account';
      setErrorMessage(errorMessageToDisplay);
      setShowPasswordResetLink(error.code === errors.emailAddressAlreadyInUse.code);
      dispatch(newUser(false));
      setAppLoadingState(dispatch, false);
    }
  };
  const showTermsAndConditionsModal = () => {
    setShowTermsAndConditions(true);
  };
  const hideTermsAndConditionsModal = () => {
    setShowTermsAndConditions(false);
  };

  const submitLight = (
    <SubmitButton
      className={classes.submitButton}
      onClick={handleSubmit}
      text={submitText}
      variant="dark"
    />);

  const submitDark = (
    <SubmitButton
      className={classes.submitButton}
      onClick={handleSubmit}
      text={submitText}
      variant="light"
    />);

  return (
    <BaseForm themeStyle={themeStyle}>
      <FormGroup className={themerizer}>
        <FormControl variant="filled" className={classes.inputBase}>
          <StandardInputComponent
            id="username"
            label="Username"
            value={formValues.displayName}
            onChange={handleChange('displayName')}
            autoCompleteOff={true}
          />
        </FormControl>

        <FormControl variant="filled" className={classes.inputBase}>
          <PasswordInputComponent
            onChange={handleChange('password')}
            id="password"
            label="Password"
            value={formValues.password}
            autoCompleteOff={true}
          />
        </FormControl>

        <FormControl variant="filled" className={classes.inputBase}>
          <PasswordInputComponent
            onChange={handleChange('confirmPass')}
            id="confirm-password"
            label="Confirm Password"
            value={formValues.confirmPass}
            autoCompleteOff={true}
          />
        </FormControl>
        <div id="email-anchor"/>
        <FormControl variant="filled" className={classes.inputBase}>
          <StandardInputComponent
            id="email"
            label="Email"
            value={formValues.email}
            onChange={handleChange('email')}
          />
        </FormControl>

        <FormControl variant="filled" className={classes.inputBase}>
          <StandardInputComponent
            id="first-name"
            label="First Name"
            value={formValues.firstName}
            onChange={handleChange('firstName')}
          />
        </FormControl>

        <FormControl variant="filled" className={classes.inputBase}>
          <StandardInputComponent
            id="last-name"
            label="Last Name"
            value={formValues.lastName}
            onChange={handleChange('lastName')}
          />
        </FormControl>

        <FormControl variant="filled" className={classes.inputBase}>
          <SelectMenuComponent
            id="timezone"
            label="Timezone"
            value={formValues.timezone}
            onChange={handleChange('timezone')}
          >
            <option aria-label="None" value=""/>
            {timezones.map(timezone => (
              <option aria-label={timezone} value={timezone}>
                {timezone}
              </option>
            ))}
          </SelectMenuComponent>
        </FormControl>

        <FormControl variant="filled" className={classes.inputBase}>
          <SelectMenuComponent
            id="referal"
            label="How did you hear about us?"
            value={formValues.referal}
            onChange={handleChange('referal')}
          >
            <option aria-label="None" value=""/>
            <option aria-label="Facebook" value="Facebook">Facebook</option>
            <option aria-label="Instagram" value="Instagram">Instagram</option>
            <option aria-label="App" value="App">App</option>
            <option aria-label="Website" value="Website">Website</option>
            <option aria-label="Friend/Family" value="Relation">Friend/Family</option>
            <option aria-label="Church" value="Church">Church</option>
            <option aria-label="Conference" value="Conference">Conference</option>
            <option aria-label="Other" value="Other">Other</option>
          </SelectMenuComponent>
        </FormControl>
      </FormGroup>

      <FormControl className={classes.base}>
        <CheckboxInputComponent
          name="agreeTermsConditions"
          value={formValues.agreeTermsConditions}
          label="I have read and agree to the &nbsp;"
          onChange={handleCheckChange}
          variant={themeStyle === 'light' ? 'dark' : 'light'}
        />
        <Typography
          className={classes.termsAndConditionsLink}
          onClick={showTermsAndConditionsModal}>Terms and Conditions.</Typography>
      </FormControl>
      {!!errorMessage &&
          <Typography variant="h6" color="error">
            {errorMessage}
          </Typography>}
      {
        themeStyle === 'light' ? submitLight : submitDark
      }

      <div>
        {showPasswordResetLink && <PasswordResetLink/>}
        {showTermsAndConditions &&
            <Modal
                open={showTermsAndConditions}
                onClose={hideTermsAndConditionsModal}
                className={classes.modal}>
                <TermsAndConditionsModal closeModal={hideTermsAndConditionsModal} title={''}/>
            </Modal>}
      </div>
      <div id="recaptcha"></div>
    </BaseForm>
  );
}

function PasswordResetLink() {
  const classes = useStyles(baseTheme);
  const openSignIn = () => {
    window.scrollTo({ top: 0 });
  };
  return (
      <div className={classes.passwordResetLinksWrapper}>
        <a
            className={classes.passwordResetLinksButton}
            href="#email-anchor"
        >
          Choose Another</a>
        <a
            className={classes.passwordResetLinksButton}
            onClick={openSignIn}
        >
            Sign In
        </a>
        <a
            className={classes.passwordResetLinksButton}
            href={ROUTE_PATHS.USER_HELP}
        >
          Forgot Password
        </a>
      </div>
  );
}
